import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import useSWR from 'swr'
import { DamageInfo, ErrorInfo, PlanMinRlsInfo, RemainingLifeInfo } from 'types'

export function GetComponentPercentBelowTmin(componentId: string | undefined) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/belowTmin?component=${componentId}`

  return useSWR<{
    totalCount: number
    belowTminCount: number
    percentBelowTmin: number
  }>(componentId ? [componentId] : null, () =>
    axios
      .get(endpoint)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function useGetBulkComponentMinRls(componentIds: string[]) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkMinRemainingLife`
  const postBody = {
    components: componentIds,
  }

  return useSWR<{
    [componentId: string]: RemainingLifeInfo | ErrorInfo
  }>(componentIds.length ? ['minRL', componentIds] : null, () =>
    axios
      .post(endpoint, postBody)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function useGetBulkPlanMinRls(planIds: string[]) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkCalculateScenarioCascade`
  const postBody = {
    plans: planIds,
  }

  return useSWR<{
    [planId: string]: {
      [componentId: string]: PlanMinRlsInfo | ErrorInfo
    } | null
  }>(planIds.length ? [planIds] : null, () =>
    axios
      .post(endpoint, postBody)
      .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export const DEFAULT_PLAN_KEY = 'baseData'

/**
 * This function returns the damage areas and RL stats for a list of components and plans.
 *
 * The return signature is a map of componentIds to a map of planKeys which contains the damage areas and RL stats.
 *
 * If no planId is supplied, the planKey in the component object is the constant DEFAULT_DAMAGE_AREAS_PLAN_KEY
 *
 */
export function useGetBulkDamageAreas(
  componentAndPlanList: {
    componentId: string
    planId?: string
  }[],
  timeHorizon?: number,
  riskScoreMethod?: string,
  useRiskAssessment?: boolean
) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RISK_CALCULATOR_URL } = useRuntimeVariables()
  const endpoint = `${RISK_CALCULATOR_URL}/api/bulkCalculateDamageAreas`
  const postBody = {
    componentAndPlanList: componentAndPlanList,
    timeHorizon: timeHorizon,
    riskScoreMethod: riskScoreMethod,
    useRiskAssessment: useRiskAssessment,
  }

  return useSWR<{
    [componentId: string]: {
      [planKey: string | typeof DEFAULT_PLAN_KEY]: DamageInfo | ErrorInfo
    }
  }>(
    componentAndPlanList.length
      ? [componentAndPlanList, riskScoreMethod, timeHorizon]
      : null,
    () =>
      axios
        .post(endpoint, postBody)
        .then((res) => (typeof res.data === 'string' ? {} : res.data))
  )
}

export function isErrorInfo(
  value: DamageInfo | RemainingLifeInfo | PlanMinRlsInfo | ErrorInfo
): value is ErrorInfo {
  return 'error' in value
}
